import * as THREE from 'three';

/** Global TWEEN reference from potree's script-loaded tween.js */
declare const TWEEN: {
  Tween: new (target: Record<string, number>) => TweenInstance;
  Easing: { Linear: { None: unknown } };
  remove: (tween: TweenInstance) => void;
};

interface TweenInstance {
  to: (target: Record<string, number>, duration: number) => TweenInstance;
  easing: (easing: unknown) => TweenInstance;
  onUpdate: (callback: (e: unknown) => void) => TweenInstance;
  onComplete: (callback: () => void) => TweenInstance;
  start: () => TweenInstance;
  stop: () => void;
}

export class PathAnimation {
  path: AnimationPath;
  length: number;
  speed: number;
  callback: (t: number) => void;
  tween: TweenInstance | null;
  startPoint: number;
  endPoint: number;
  t: number;
  repeat?: boolean;

  constructor(path: AnimationPath, start: number, end: number, speed: number, callback: (t: number) => void) {
    this.path = path;
    this.length = this.path.spline.getLength();
    this.speed = speed;
    this.callback = callback;
    this.tween = null;
    this.startPoint = Math.max(start, 0);
    this.endPoint = Math.min(end, this.length);
    this.t = 0.0;
  }

  start(resume = false) {
    if (this.tween) {
      this.tween.stop();
      this.tween = null;
    }

    let tStart: number;
    if (resume) {
      tStart = this.t;
    } else {
      tStart = this.startPoint / this.length;
    }
    const tEnd = this.endPoint / this.length;
    const animationDuration = ((tEnd - tStart) * this.length * 1000) / this.speed;

    const progress = { t: tStart };
    this.tween = new TWEEN.Tween(progress).to({ t: tEnd }, animationDuration);
    this.tween.easing(TWEEN.Easing.Linear.None);
    this.tween.onUpdate(() => {
      this.t = progress.t;
      this.callback(progress.t);
    });
    this.tween.onComplete(() => {
      if (this.repeat) {
        this.start();
      }
    });

    setTimeout(() => {
      if (this.tween) {
        this.tween.start();
      }
    }, 0);
  }

  stop() {
    if (!this.tween) {
      return;
    }
    this.tween.stop();
    this.tween = null;
    this.t = 0;
  }

  pause() {
    if (!this.tween) {
      return;
    }

    this.tween.stop();
    TWEEN.remove(this.tween);
    this.tween = null;
  }

  resume() {
    this.start(true);
  }

  getPoint(t: number) {
    return this.path.spline.getPoint(t);
  }
}

export class AnimationPath {
  points: THREE.Vector3[];
  spline: THREE.CatmullRomCurve3;
  tween: TweenInstance | null;

  constructor(points: THREE.Vector3[] = []) {
    this.points = points;
    this.spline = new THREE.CatmullRomCurve3(points);
    this.tween = null;
    //this.spline.reparametrizeByArcLength(1 / this.spline.getLength().total);
  }

  get(t: number) {
    return this.spline.getPoint(t);
  }

  getLength() {
    return this.spline.getLength();
  }

  animate(start: number, end: number, speed: number, callback: (t: number) => void) {
    const animation = new PathAnimation(this, start, end, speed, callback);
    animation.start();

    return animation;
  }

  pause() {
    if (this.tween) {
      this.tween.stop();
    }
  }

  resume() {
    if (this.tween) {
      this.tween.start();
    }
  }

  getGeometry() {
    const geometry = new THREE.BufferGeometry();

    const samples = 500;
    const positions: number[] = [];
    for (let u = 0; u <= 1; u += 1 / samples) {
      const position = this.spline.getPoint(u);
      positions.push(position.x, position.y, position.z);
    }

    if (this.closed) {
      const position = this.spline.getPoint(0);
      positions.push(position.x, position.y, position.z);
    }

    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

    return geometry;
  }

  get closed() {
    return this.spline.closed;
  }

  set closed(value: boolean) {
    this.spline.closed = value;
  }
}
